import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "../../lib/api";
import { viewportOnce } from "../../lib/motion";

export const Gallery = () => {
  const [images, setImages] = useState([]);
  const [active, setActive] = useState(null); // index

  useEffect(() => {
    api.get("/gallery").then((r) => setImages(r.data)).catch(() => {});
  }, []);

  const close = () => setActive(null);
  const prev = (e) => {
    e?.stopPropagation();
    setActive((a) => (a - 1 + images.length) % images.length);
  };
  const next = (e) => {
    e?.stopPropagation();
    setActive((a) => (a + 1) % images.length);
  };

  useEffect(() => {
    if (active === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, images.length]);

  return (
    <section id="gallery" data-testid="gallery-section" className="bg-cream py-24 md:py-32">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="max-w-2xl mb-16">
          <span className="label-caps text-sage">Gallery</span>
          <h2 className="mt-4 font-serif text-4xl md:text-5xl lg:text-6xl text-ink leading-[1.05]">
            A taste of our <span className="italic text-gold">craft</span>
          </h2>
        </div>

        <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:_balance]">
          {images.map((img, i) => (
            <motion.button
              key={img.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={viewportOnce}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              data-testid={`gallery-item-${i}`}
              onClick={() => setActive(i)}
              className="mb-5 block w-full overflow-hidden group relative"
            >
              <img
                src={img.image}
                alt={img.caption || "OKOME catering"}
                loading="lazy"
                className="w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-charcoal/0 group-hover:bg-charcoal/30 transition-colors duration-500 flex items-end p-5">
                <span className="text-cream label-caps opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  {img.caption}
                </span>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active !== null && images[active] && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
            data-testid="gallery-lightbox"
            className="fixed inset-0 z-[70] bg-charcoal/95 backdrop-blur-sm flex items-center justify-center p-4"
          >
            <button
              onClick={close}
              data-testid="lightbox-close"
              className="absolute top-6 right-6 text-cream/80 hover:text-gold"
              aria-label="Close"
            >
              <X size={30} />
            </button>
            <button
              onClick={prev}
              className="absolute left-4 md:left-10 text-cream/80 hover:text-gold"
              aria-label="Previous"
            >
              <ChevronLeft size={40} />
            </button>
            <motion.figure
              key={active}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-4xl max-h-[85vh]"
            >
              <img
                src={images[active].image}
                alt={images[active].caption}
                className="max-h-[78vh] w-auto mx-auto object-contain"
              />
              <figcaption className="text-center text-cream/70 mt-4 font-serif italic text-xl">
                {images[active].caption}
              </figcaption>
            </motion.figure>
            <button
              onClick={next}
              className="absolute right-4 md:right-10 text-cream/80 hover:text-gold"
              aria-label="Next"
            >
              <ChevronRight size={40} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
